// MLMenu.js
"use strict";

// Detect animation end event
const animEndEventNames = {
    WebkitAnimation: "webkitAnimationEnd",
    OAnimation: "oAnimationEnd",
    msAnimation: "MSAnimationEnd",
    animation: "animationend",
};

const testEl = document.createElement("div");
let animEndEventName = "animationend";
for (let key in animEndEventNames) {
    if (testEl.style[key] !== undefined) {
        animEndEventName = animEndEventNames[key];
        break;
    }
}

// Utility: run callback when animation ends
function onEndAnimation(el, callback) {
    if (!el) return;
    el.addEventListener(
        animEndEventName,
        (ev) => {
            if (ev.target !== el) return;
            if (typeof callback === "function") callback();
        },
        { once: true }
    );
}

export default class MLMenu {
    constructor(el, options = {}) {
        this.el = el;
        this.options = Object.assign(
            {
                breadcrumbsCtrl: false,
                // initialBreadcrumb: "all",
                backCtrl: true,
                itemsDelayInterval: 60,
                direction: "r2l",
                onItemClick: (ev, itemName) => false,
            },
            options
        );

        this.menus = [...this.el.querySelectorAll(".menu__level")];
        this.current_menu = 0;
        this.isAnimating = false;
        this.menusArr = [];
        this.breadCrumbs = false;

        // Detect initial menu
        this.menus.forEach((menuEl, pos) => {
            menuEl.querySelectorAll(".menu__item").forEach((itemEl) => {
                if (itemEl.querySelector(".menu__link--current")) {
                    this.current_menu = pos;
                }
            });
        });

        this._init();
    }

    _init() {
        const submenus = [];

        // Build menu structure
        this.menus.forEach((menuEl, pos) => {
            const menu = {
                menuEl,
                menuItems: [...menuEl.querySelectorAll(".menu__item")],
            };
            this.menusArr.push(menu);

            if (pos === this.current_menu) {
                menuEl.classList.add("menu__level--current");
            }

            menuEl.querySelectorAll(".menu__link").forEach((linkEl) => {
                const submenu = linkEl.getAttribute("data-submenu");
                if (submenu) {
                    const entry = { menu: submenu, name: linkEl.innerHTML };
                    submenus[pos] = submenus[pos] || [];
                    submenus[pos].push(entry);
                }
            });
        });

        // Assign parent relationships
        this.menus.forEach((menuEl, pos) => {
            const menuId = menuEl.getAttribute("data-menu");
            submenus.forEach((subMenuList, parentIdx) => {
                subMenuList.forEach((subMenuItem) => {
                    if (subMenuItem.menu === menuId) {
                        this.menusArr[pos].backIdx = parentIdx;
                        this.menusArr[pos].name = subMenuItem.name;
                    }
                });
            });
        });

        // Breadcrumbs
        if (this.options.breadcrumbsCtrl) {
            this.breadcrumbsCtrl = document.createElement("nav");
            this.breadcrumbsCtrl.className = "menu__breadcrumbs";
            this.breadcrumbsCtrl.setAttribute("aria-label", "You are here");
            this.el.prepend(this.breadcrumbsCtrl);
            this._addBreadcrumb(0);

            if (
                this.menusArr[this.current_menu].backIdx !== 0 &&
                this.current_menu !== 0
            ) {
                this._crawlCrumbs(this.menusArr[this.current_menu].backIdx);
                this.breadCrumbs = true;
            }
            if (this.current_menu !== 0) {
                this._addBreadcrumb(this.current_menu);
                this.breadCrumbs = true;
            }
        }

        // Back button
        if (this.options.backCtrl) {
            this.backCtrl = document.createElement("button");
            this.backCtrl.className = this.breadCrumbs
                ? "menu__back"
                : "menu__back menu__back--hidden";
            this.backCtrl.setAttribute("aria-label", "Go back");
            this.backCtrl.innerHTML = '<span class="icon icon--arrow-left"></span>';
            this.el.prepend(this.backCtrl);
        }

        this._initEvents();
    }

    _initEvents() {
        this.menusArr.forEach((menu) => {
            menu.menuItems.forEach((item) => {
                const link = item.querySelector("a");
                if (!link) return;

                link.addEventListener("click", (ev) => {
                    const submenu = ev.target.getAttribute("data-submenu");
                    const itemName = ev.target.innerHTML;
                    const subMenuEl = this.el.querySelector(
                        `ul[data-menu="${submenu}"]`
                    );

                    if (submenu && subMenuEl) {
                        ev.preventDefault();
                        this._openSubMenu(
                            subMenuEl,
                            menu.menuItems.indexOf(item),
                            itemName
                        );
                    } else {
                        const currentLink = this.el.querySelector(
                            ".menu__link--current"
                        );
                        if (currentLink) {
                            currentLink.classList.remove("menu__link--current");
                        }
                        ev.target.classList.add("menu__link--current");
                        this.options.onItemClick(ev, itemName);
                    }
                });
            });
        });

        if (this.options.backCtrl) {
            this.backCtrl.addEventListener("click", () => this._back());
        }
    }

    _openSubMenu(subMenuEl, clickPosition, name) {
        if (this.isAnimating) return;
        this.isAnimating = true;

        const idx = this.menus.indexOf(subMenuEl);
        this.menusArr[idx].backIdx = this.current_menu;
        this.menusArr[idx].name = name;

        this._menuOut(clickPosition);
        this._menuIn(subMenuEl, clickPosition);
    }

    _back() {
        if (this.isAnimating) return;
        this.isAnimating = true;

        this._menuOut();
        const backMenu =
            this.menusArr[this.menusArr[this.current_menu].backIdx].menuEl;
        this._menuIn(backMenu);

        if (this.options.breadcrumbsCtrl) {
            this.breadcrumbsCtrl.removeChild(
                this.breadcrumbsCtrl.lastElementChild
            );
        }
    }

    _menuOut(clickPosition) {
        const currentMenu = this.menusArr[this.current_menu].menuEl;
        const isBack = clickPosition === undefined;

        this.menusArr[this.current_menu].menuItems.forEach((item, pos) => {
            const delay = isBack
                ? pos * this.options.itemsDelayInterval
                : Math.abs(clickPosition - pos) * this.options.itemsDelayInterval;
            item.style.animationDelay = `${delay}ms`;
        });

        if (this.options.direction === "r2l") {
            currentMenu.classList.add(
                isBack ? "animate-outToRight" : "animate-outToLeft"
            );
        } else {
            currentMenu.classList.add(
                isBack ? "animate-outToLeft" : "animate-outToRight"
            );
        }
    }

    _menuIn(nextMenuEl, clickPosition) {
        const currentMenu = this.menusArr[this.current_menu].menuEl;
        const isBack = clickPosition === undefined;
        const nextIdx = this.menus.indexOf(nextMenuEl);
        const nextMenu = this.menusArr[nextIdx];
        const nextItems = nextMenu.menuItems;
        const total = nextItems.length;

        nextItems.forEach((item, pos) => {
            const delay = isBack
                ? pos * this.options.itemsDelayInterval
                : Math.abs(clickPosition - pos) * this.options.itemsDelayInterval;
            item.style.animationDelay = `${delay}ms`;

            const farthestIdx =
                clickPosition <= total / 2 || isBack ? total - 1 : 0;

            if (pos === farthestIdx) {
                onEndAnimation(item, () => {
                    currentMenu.classList.remove(
                        "animate-outToLeft",
                        "animate-outToRight",
                        "menu__level--current"
                    );
                    nextMenuEl.classList.remove(
                        "animate-inFromRight",
                        "animate-inFromLeft"
                    );
                    nextMenuEl.classList.add("menu__level--current");
                    this.current_menu = nextIdx;

                    if (!isBack) {
                        if (this.options.backCtrl) {
                            this.backCtrl.classList.remove("menu__back--hidden");
                        }
                        this._addBreadcrumb(nextIdx);
                    } else if (this.current_menu === 0 && this.options.backCtrl) {
                        this.backCtrl.classList.add("menu__back--hidden");
                    }

                    this.isAnimating = false;
                    nextMenuEl.setAttribute("tabindex", "-1");
                    nextMenuEl.focus();
                });
            }
        });

        if (this.options.direction === "r2l") {
            nextMenuEl.classList.add(
                isBack ? "animate-inFromLeft" : "animate-inFromRight"
            );
        } else {
            nextMenuEl.classList.add(
                isBack ? "animate-inFromRight" : "animate-inFromLeft"
            );
        }
    }

    _addBreadcrumb(idx) {
        if (!this.options.breadcrumbsCtrl) return;

        const bc = document.createElement("button");
        bc.type = "button";
        bc.className = "menu__breadcrumb";
        bc.innerHTML = idx
            ? this.menusArr[idx].name
            : this.options.initialBreadcrumb;
        this.breadcrumbsCtrl.appendChild(bc);

        bc.addEventListener("click", (ev) => {
            ev.preventDefault();
            if (!bc.nextSibling || this.isAnimating) return;
            this.isAnimating = true;

            this._menuOut();
            this._menuIn(this.menusArr[idx].menuEl);

            while (bc.nextSibling) {
                this.breadcrumbsCtrl.removeChild(bc.nextSibling);
            }
        });
    }

    _crawlCrumbs(idx) {
        if (this.menusArr[idx].backIdx !== 0) {
            this._crawlCrumbs(this.menusArr[idx].backIdx);
        }
        this._addBreadcrumb(idx);
    }
}
